/**
 * Frame-rate detection for loaded videos. The browser exposes no fps metadata,
 * so we briefly play the (muted) video and sample presented-frame mediaTime
 * deltas via requestVideoFrameCallback, then snap to the common rates.
 * Falls back to 30 when rVFC is unavailable or the clip is too short to sample.
 */

const COMMON_FPS = [23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60, 90, 120];
const SAMPLES = 12;
const SAMPLE_TIMEOUT_MS = 1500;

export async function detectFps(video: HTMLVideoElement): Promise<number> {
  if (!('requestVideoFrameCallback' in video)) return 30;

  const times: number[] = [];
  await new Promise<void>((resolve) => {
    let handle = 0;
    let finished = false;
    const finish = (): void => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      video.cancelVideoFrameCallback(handle);
      resolve();
    };
    const timer = setTimeout(finish, SAMPLE_TIMEOUT_MS);
    const onFrame: VideoFrameRequestCallback = (_now, meta) => {
      times.push(meta.mediaTime);
      if (times.length >= SAMPLES) {
        finish();
        return;
      }
      handle = video.requestVideoFrameCallback(onFrame);
    };
    handle = video.requestVideoFrameCallback(onFrame);
    video.muted = true;
    void video.play().catch(() => finish());
  });

  video.pause();
  video.currentTime = 0;

  const deltas: number[] = [];
  for (let i = 1; i < times.length; i++) {
    const d = times[i] - times[i - 1];
    if (d > 0.0005) deltas.push(d);
  }
  if (deltas.length < 3) return 30;

  deltas.sort((a, b) => a - b);
  const median = deltas[Math.floor(deltas.length / 2)];
  const raw = 1 / median;

  let best: number | null = null;
  let bestErr = Infinity;
  for (const fps of COMMON_FPS) {
    const err = Math.abs(raw - fps) / fps;
    if (err < 0.05 && err < bestErr) {
      best = fps;
      bestErr = err;
    }
  }
  if (best !== null) return best;
  return Math.min(240, Math.max(5, Math.round(raw * 1000) / 1000));
}
